import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { getApplications, getDisabledPlugins } from '@app/common/store/profiles/selectors';
import { ControlledModal } from '@app/common/modals';
import { useUserPluginRestrictedMode } from '@app/pages/profiles/profile-details/profile-ide-provisioner/hooks/user-restricted-modes';
import { disablePluginsToBundle, pluginRuleBundleToDisablePlugins } from '@modules/ide-provisioner/plugins-rules/common/disabled-plugins-legacy-model-converter';
import { usePluginData } from '@modules/plugins/use-plugin-data';
import { LegacyFeatureType } from '@modules/ide-provisioner/plugins-rules/plugin-rules-bundle-dialog/legacy-feature-type';
import { PluginRulesBundleDialog } from '@modules/ide-provisioner/plugins-rules/plugin-rules-bundle-dialog/plugin-rules-bundle-dialog';
import { ProfileActions } from '@common/store/profiles/profile-reducer';
import { SettingsList } from '../../common/settings-list';
import SettingsListEntry from '../../common/settings-list-entry';
import { SettingPlaceholder } from '../../common/settings-placeholder';
import { useSettingsContext } from '../../context/settings-context';
import { showFilteredSettings, showHiddenSettingsPlaceholder } from '../../functions/settings';
import { filterByFocusedApp, useProfilesParams } from '../../context/profiles-params-context';
export function DisabledPlugins(props) {
    var _a, _b;
    const { profileId } = props;
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const plugins = useSelector(getDisabledPlugins);
    const { focusedApp } = useProfilesParams();
    const filteredPlugins = filterByFocusedApp(plugins, focusedApp);
    const applications = useSelector(getApplications);
    const { isRestricted } = useUserPluginRestrictedMode();
    const { setFormState, showForm, resetFormState, settingId } = useSettingsContext('disabled');
    const handleDeleteClick = (settingId) => {
        dispatch(ProfileActions.deleteSetting({ settingType: 'disabled', settingId }));
    };
    const submitContentForm = useCallback((values) => {
        dispatch(ProfileActions.updateDisabledPlugin(values));
        setIsCollapsed(false);
        resetFormState();
    }, [resetFormState, dispatch, setIsCollapsed]);
    const handleOnCloseDalogAttempt = useCallback((values) => {
        if (values) {
            submitContentForm(pluginRuleBundleToDisablePlugins(values.bundle, values.tools));
        }
        else {
            resetFormState();
        }
    }, [submitContentForm, resetFormState]);
    const disabledPluginsSetting = useMemo(() => plugins.find(({ id }) => id === settingId), [plugins, settingId]);
    const pluginDataRequired = !!((_a = disabledPluginsSetting === null || disabledPluginsSetting === void 0 ? void 0 : disabledPluginsSetting.content) === null || _a === void 0 ? void 0 : _a.length);
    const { data: pluginsData, isFetching: isPluginsDataFetching } = usePluginData((_b = disabledPluginsSetting === null || disabledPluginsSetting === void 0 ? void 0 : disabledPluginsSetting.content.map(value => value.pluginId)) !== null && _b !== void 0 ? _b : [], pluginDataRequired);
    const pluginRulesBundle = useMemo(() => {
        if (!disabledPluginsSetting) {
            return undefined;
        }
        return disablePluginsToBundle(disabledPluginsSetting, pluginsData);
    }, [disabledPluginsSetting, pluginsData]);
    return (_jsxs(_Fragment, { children: [_jsxs(SettingsList, { title: "disabled plugins", isCollapsed: isCollapsed, onToggleCollapsed: () => setIsCollapsed(prev => !prev), children: [isEmpty(plugins) && _jsx(SettingPlaceholder, { title: "No disabled plugins yet" }), showHiddenSettingsPlaceholder(plugins, filteredPlugins) && (_jsx(SettingPlaceholder, { title: plugins.length === 1 ? 'One plugin is hidden' : `${plugins.length} plugins are hidden` })), showFilteredSettings(filteredPlugins) &&
                        filteredPlugins.map(entry => (_jsx(SettingsListEntry, { entry: entry, onEditClick: setFormState, onDeleteClick: handleDeleteClick, settingType: "disabled" }, entry.id)))] }), showForm && !(pluginDataRequired && isPluginsDataFetching) && (_jsx(ControlledModal, { component: PluginRulesBundleDialog, dialogProps: {
                    pluginRulesBundle,
                    profileId,
                    applicationFilters: applications.rules,
                    legacyFeatureType: LegacyFeatureType.DisabledPlugins,
                    isRestrictedMode: isRestricted
                }, onCloseAttempt: handleOnCloseDalogAttempt }))] }));
}
